import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { PublicKey } from '@solana/web3.js';
import { getAssociatedTokenAddressSync } from '@solana/spl-token';
import { parseError } from '../utils/errorParser';
import { validateAmount, sanitizeAmount } from '../utils/validation';
import { copyToClipboard } from '../utils/clipboard';
import { useWalletRouter } from './useWalletRouter';
import { useDiagnostics } from './useDiagnostics';
import { setObservabilityContext, setObservabilityTag } from '../observability/sentry';
import { CHAIN_KEYS } from '../chains/interfaces';
import {
  getDefaultChainKey,
  getEnabledChains,
  getComingSoonChains,
  getChainConfig,
} from '../chains/registry';
import {
  approveIfRequired,
  checkAllowance,
  fetchBalances,
  getCapabilities,
  simulateSwap,
  submitSwap,
} from '../orchestrator/swapOrchestrator';

export const SELECTED_CHAIN_STORAGE_KEY = 'usdn:selected-swap-chain:v2';
export const SELECTED_STABLECOIN_STORAGE_PREFIX = 'usdn:selected-stablecoin:v1';

export const deriveSolanaAta = ({ ownerAddress, mintAddress, tokenProgramId }) => {
  if (!ownerAddress || !mintAddress || !tokenProgramId) return '';
  try {
    return getAssociatedTokenAddressSync(
      new PublicKey(mintAddress),
      new PublicKey(ownerAddress),
      false,
      new PublicKey(tokenProgramId)
    ).toBase58();
  } catch {
    return '';
  }
};

export const getInitialChainKey = () => {
  if (typeof window === 'undefined') return getDefaultChainKey();
  try {
    const storedChainKey = window.localStorage.getItem(SELECTED_CHAIN_STORAGE_KEY);
    const enabledChainKeys = new Set(getEnabledChains().map(chain => chain.key));
    if (storedChainKey && enabledChainKeys.has(storedChainKey)) return storedChainKey;
  } catch {
    // Ignore storage failures and use the registry default.
  }
  return getDefaultChainKey();
};

export const getStablecoinStorageKey = chainKey =>
  `${SELECTED_STABLECOIN_STORAGE_PREFIX}:${chainKey}`;

export const getInitialStablecoinKey = chainKey => {
  const configuredStablecoins = getChainConfig(chainKey)?.stablecoins || {};
  if (typeof window !== 'undefined') {
    try {
      const storedStablecoin = window.localStorage.getItem(getStablecoinStorageKey(chainKey));
      if (storedStablecoin && configuredStablecoins[storedStablecoin]) return storedStablecoin;
    } catch {
      // Ignore storage failures and use the registry default for the chain.
    }
  }
  return Object.keys(configuredStablecoins)[0] || 'USDT';
};

/**
 * Headless controller hook managing swap state, wallet connection,
 * balance fetching, approvals, transactions, and storage synchronization.
 */
export const useSwapController = () => {
  const enabledChains = useMemo(() => getEnabledChains(), []);
  const comingSoonChains = useMemo(() => getComingSoonChains(enabledChains), [enabledChains]);
  const [selectedChainKey, setSelectedChainKey] = useState(getInitialChainKey);
  const selectedChain = useMemo(() => getChainConfig(selectedChainKey), [selectedChainKey]);
  const stablecoinEntries = useMemo(
    () => Object.entries(selectedChain?.stablecoins || {}),
    [selectedChain]
  );

  const [amount, setAmount] = useState('');
  const [status, setStatus] = useState('');
  const [statusType, setStatusType] = useState('');
  const [selectedStablecoin, setSelectedStablecoin] = useState(() =>
    getInitialStablecoinKey(getInitialChainKey())
  );
  const [isLoading, setIsLoading] = useState(false);
  const [transactionHash, setTransactionHash] = useState('');
  const [copyFeedback, setCopyFeedback] = useState(false);
  const [showMaxModal, setShowMaxModal] = useState(false);
  const [stablecoinBalance, setStablecoinBalance] = useState('0');
  const [usdnBalance, setUsdnBalance] = useState('0');
  const [stablecoinLoading, setStablecoinLoading] = useState(false);
  const [usdnLoading, setUsdnLoading] = useState(false);
  const [allowanceEnough, setAllowanceEnough] = useState(false);
  const [allowanceLoading, setAllowanceLoading] = useState(false);
  const [pendingSwapHash, setPendingSwapHash] = useState('');
  const [balanceDiagnostics, setBalanceDiagnostics] = useState(null);
  const [isChainMenuOpen, setIsChainMenuOpen] = useState(false);
  const [isNetworkSwitchModalOpen, setIsNetworkSwitchModalOpen] = useState(false);

  const balanceRequestIdRef = useRef(0);
  const dismissedNetworkSwitchKeyRef = useRef('');

  const wallet = useWalletRouter(selectedChainKey);
  const {
    address,
    isConnected,
    isCorrectNetwork,
    currentChainId,
    currentNetworkName,
    toggleTargetNetworkName,
    connect,
    disconnect,
    switchNetwork,
    provider: walletProvider,
    solanaWallet,
    walletProviderLabel,
  } = wallet;

  const selectedCoin = selectedChain?.stablecoins?.[selectedStablecoin];
  const balanceLabel = `${selectedChain?.networkLabel || selectedChain?.name || 'Selected chain'} balance`;
  const decimals = selectedCoin?.decimals || 6;
  const capabilities = getCapabilities(selectedChainKey);

  const isSelectedChainConfigured = useMemo(() => {
    if (!selectedChain) return false;
    if (selectedChain.adapter === 'evm') {
      return Boolean(
        selectedChain.usdnAddress &&
        selectedChain.swapContractAddress &&
        selectedChain.approveContractAddress &&
        selectedCoin?.address
      );
    }
    if (selectedChain.adapter === 'solana') {
      return Boolean(
        selectedChain.usdnMint &&
        selectedChain.usdnTokenProgramId &&
        selectedChain.swapProgramId &&
        selectedChain.instructionDiscriminatorHex &&
        selectedCoin?.mint &&
        selectedCoin?.tokenProgramId
      );
    }
    return true;
  }, [selectedChain, selectedCoin]);

  const chainReadinessMessage = useMemo(() => {
    if (isSelectedChainConfigured) return '';
    if (selectedChain?.readinessMessage) return selectedChain.readinessMessage;
    return `${selectedChain?.networkLabel || 'Selected mainnet'} is listed, but swap contracts or token addresses are not configured yet.`;
  }, [isSelectedChainConfigured, selectedChain]);

  const networkDisplayName = useMemo(() => {
    const raw = (currentNetworkName || selectedChain?.networkLabel || '').trim();
    if (!raw) return 'Unknown';
    if (selectedChain?.name && raw.toLowerCase().startsWith(selectedChain.name.toLowerCase())) {
      const stripped = raw.slice(selectedChain.name.length).trim();
      return stripped || raw;
    }
    if (selectedChainKey === CHAIN_KEYS.SOLANA && raw.toLowerCase().startsWith('solana ')) {
      return raw.replace(/^solana\s+/i, '').trim();
    }
    if (selectedChainKey === CHAIN_KEYS.ETHEREUM && raw.toLowerCase().startsWith('ethereum ')) {
      return raw.replace(/^ethereum\s+/i, '').trim();
    }
    return raw;
  }, [currentNetworkName, selectedChain, selectedChainKey]);

  const isWalletNetworkAligned = useMemo(() => {
    if (selectedChain?.adapter !== 'evm') return isCorrectNetwork;
    if (!isConnected || !currentChainId) return isCorrectNetwork;
    return isCorrectNetwork || Number(currentChainId) === Number(selectedChain?.networkId);
  }, [currentChainId, isConnected, isCorrectNetwork, selectedChain]);

  useEffect(() => {
    setObservabilityTag('chain.key', selectedChainKey);
    setObservabilityTag('chain.adapter', selectedChain?.adapter || 'unknown');
    setObservabilityTag('network.label', selectedChain?.networkLabel || 'unknown');
    setObservabilityContext('wallet', {
      connected: isConnected,
      correctNetwork: isWalletNetworkAligned,
      chainId: currentChainId,
      selectedChainKey,
    });
  }, [currentChainId, isConnected, isWalletNetworkAligned, selectedChain, selectedChainKey]);

  useEffect(() => {
    if (!isConnected) {
      dismissedNetworkSwitchKeyRef.current = '';
      setIsNetworkSwitchModalOpen(false);
    }
  }, [isConnected]);

  useEffect(() => {
    const mismatchKey = `${address || 'unknown'}:${selectedChainKey}:${currentChainId || 'unknown'}`;
    const hasCurrentMismatch =
      isConnected && selectedChain?.adapter === 'evm' && currentChainId && !isWalletNetworkAligned;

    if (!hasCurrentMismatch) {
      setIsNetworkSwitchModalOpen(false);
      if (isWalletNetworkAligned) dismissedNetworkSwitchKeyRef.current = '';
      return;
    }

    if (dismissedNetworkSwitchKeyRef.current !== mismatchKey) {
      setIsNetworkSwitchModalOpen(true);
    }
  }, [
    address,
    currentChainId,
    isConnected,
    isWalletNetworkAligned,
    selectedChain,
    selectedChainKey,
  ]);

  const amountValidation = useMemo(() => {
    if (!isConnected || !amount || stablecoinLoading) {
      return { valid: false, error: '' };
    }
    return validateAmount(amount, stablecoinBalance, decimals);
  }, [isConnected, amount, stablecoinLoading, stablecoinBalance, decimals]);

  const walletContext = useMemo(
    () => ({
      provider: walletProvider,
      address,
      solanaWallet,
    }),
    [walletProvider, address, solanaWallet]
  );

  const balanceContextKey = useMemo(
    () =>
      [
        selectedChainKey,
        address || 'disconnected',
        selectedStablecoin,
        selectedCoin?.address || selectedCoin?.mint || 'unconfigured',
      ].join(':'),
    [selectedChainKey, address, selectedStablecoin, selectedCoin]
  );

  const pendingSwapStorageKey = useMemo(
    () => (address ? `usdn:pending-swap:${selectedChainKey}:${address.toLowerCase()}` : ''),
    [selectedChainKey, address]
  );

  const pendingSwapMessage = useMemo(
    () =>
      selectedChain?.adapter === 'evm'
        ? 'A swap transaction is still queued in your wallet. Confirm, speed up, or cancel it before submitting another swap.'
        : 'A swap transaction has already been submitted. Confirm it in your wallet or explorer before submitting another swap.',
    [selectedChain]
  );

  const pendingSwapClearLabel = useMemo(
    () =>
      selectedChain?.adapter === 'evm'
        ? 'I cleared this in my wallet'
        : 'I verified this transaction',
    [selectedChain]
  );

  const connectButtonText = useMemo(
    () => (selectedChain?.adapter === 'solana' ? 'Connect Solana Wallet' : 'Connect Wallet'),
    [selectedChain]
  );

  const solanaBalanceTargets = useMemo(() => {
    if (selectedChainKey !== CHAIN_KEYS.SOLANA) {
      return { inputAta: '', usdnAta: '' };
    }
    return {
      inputAta: deriveSolanaAta({
        ownerAddress: address,
        mintAddress: selectedCoin?.mint,
        tokenProgramId: selectedCoin?.tokenProgramId,
      }),
      usdnAta: deriveSolanaAta({
        ownerAddress: address,
        mintAddress: selectedChain?.usdnMint,
        tokenProgramId: selectedChain?.usdnTokenProgramId,
      }),
    };
  }, [address, selectedChain, selectedChainKey, selectedCoin]);

  useEffect(() => {
    if (!pendingSwapStorageKey) {
      setPendingSwapHash('');
      return;
    }
    try {
      const storedHash = window.localStorage.getItem(pendingSwapStorageKey) || '';
      setPendingSwapHash(storedHash);
      if (storedHash) {
        setTransactionHash(storedHash);
        setStatus(pendingSwapMessage);
        setStatusType('error');
      }
    } catch {
      setPendingSwapHash('');
    }
  }, [pendingSwapMessage, pendingSwapStorageKey]);

  useEffect(() => {
    if (!stablecoinEntries.length) return;
    if (!selectedChain?.stablecoins?.[selectedStablecoin]) {
      setSelectedStablecoin(getInitialStablecoinKey(selectedChainKey));
    }
  }, [selectedChain, selectedChainKey, selectedStablecoin, stablecoinEntries]);

  useEffect(() => {
    if (!selectedChain?.stablecoins?.[selectedStablecoin]) return;
    try {
      window.localStorage.setItem(getStablecoinStorageKey(selectedChainKey), selectedStablecoin);
    } catch {
      // Ignore storage failures; selection still works for the current session.
    }
  }, [selectedChain, selectedChainKey, selectedStablecoin]);

  useEffect(() => {
    balanceRequestIdRef.current += 1;
    setStablecoinBalance('0');
    setUsdnBalance('0');
    setStablecoinLoading(false);
    setUsdnLoading(false);
    setBalanceDiagnostics(null);
  }, [balanceContextKey]);

  const refreshBalances = useCallback(async () => {
    const requestId = balanceRequestIdRef.current + 1;
    balanceRequestIdRef.current = requestId;
    const baseDiagnostics = {
      requestId,
      status: 'started',
      startedAt: new Date().toISOString(),
      chainKey: selectedChainKey,
      chainName: selectedChain?.name || '',
      adapter: selectedChain?.adapter || '',
      walletAdapter:
        selectedChain?.adapter === 'solana'
          ? solanaWallet?.wallet?.adapter?.name || ''
          : walletProviderLabel || 'EVM Wallet',
      walletConnected: Boolean(isConnected),
      account: address || '',
      selectedStablecoin,
      inputAddress: selectedCoin?.address || selectedCoin?.mint || '',
      inputAta: solanaBalanceTargets.inputAta,
      inputTokenProgram: selectedCoin?.tokenProgramId || '',
      usdnAddress: selectedChain?.usdnAddress || selectedChain?.usdnMint || '',
      usdnAta: solanaBalanceTargets.usdnAta,
      usdnTokenProgram: selectedChain?.usdnTokenProgramId || '',
    };
    setBalanceDiagnostics(baseDiagnostics);
    const isWrongEvmNetwork = selectedChain?.adapter === 'evm' && !isWalletNetworkAligned;
    if (
      !isConnected ||
      !selectedCoin ||
      !address ||
      !isSelectedChainConfigured ||
      isWrongEvmNetwork
    ) {
      setStablecoinBalance('0');
      setUsdnBalance('0');
      setStablecoinLoading(false);
      setUsdnLoading(false);
      setBalanceDiagnostics({
        ...baseDiagnostics,
        status: 'skipped',
        finishedAt: new Date().toISOString(),
        reason: !isConnected
          ? 'wallet not connected'
          : !selectedCoin
            ? 'selected token missing'
            : !address
              ? 'wallet address missing'
              : !isSelectedChainConfigured
                ? 'selected chain not configured'
                : 'wrong EVM network',
      });
      return;
    }
    setStablecoinLoading(true);
    setUsdnLoading(true);
    try {
      const balances = await fetchBalances({
        chainKey: selectedChainKey,
        walletContext,
        inputAsset: selectedCoin,
      });
      if (balanceRequestIdRef.current !== requestId) return;
      setStablecoinBalance(balances.inputBalance);
      setUsdnBalance(balances.outputBalance);
      setBalanceDiagnostics({
        ...baseDiagnostics,
        status: 'success',
        finishedAt: new Date().toISOString(),
        inputBalance: balances.inputBalance,
        outputBalance: balances.outputBalance,
        adapterDiagnostics: balances.diagnostics || null,
      });
    } catch (error) {
      if (balanceRequestIdRef.current !== requestId) return;
      console.error('Balance refresh error:', error);
      setStablecoinBalance('0');
      setUsdnBalance('0');
      setBalanceDiagnostics({
        ...baseDiagnostics,
        status: 'error',
        finishedAt: new Date().toISOString(),
        error: parseError(error),
        rawError: error?.message || String(error),
      });
    } finally {
      if (balanceRequestIdRef.current !== requestId) return;
      setStablecoinLoading(false);
      setUsdnLoading(false);
    }
  }, [
    isConnected,
    selectedCoin,
    address,
    isSelectedChainConfigured,
    selectedChainKey,
    selectedChain,
    selectedStablecoin,
    solanaBalanceTargets,
    solanaWallet,
    walletContext,
    isWalletNetworkAligned,
    walletProviderLabel,
  ]);

  useEffect(() => {
    refreshBalances();
  }, [refreshBalances]);

  const refreshAllowance = useCallback(async () => {
    if (
      !capabilities.requiresApproval ||
      !isConnected ||
      (selectedChain?.adapter === 'evm' && (!walletProvider || !isWalletNetworkAligned)) ||
      !selectedCoin ||
      !amount ||
      !address ||
      !isSelectedChainConfigured
    ) {
      setAllowanceEnough(!capabilities.requiresApproval);
      return;
    }
    setAllowanceLoading(true);
    try {
      const result = await checkAllowance({
        chainKey: selectedChainKey,
        walletContext,
        inputAsset: selectedCoin,
        amount,
      });
      setAllowanceEnough(result.enough);
    } catch (error) {
      console.error('Allowance refresh error:', error);
      setAllowanceEnough(false);
    } finally {
      setAllowanceLoading(false);
    }
  }, [
    capabilities.requiresApproval,
    isConnected,
    selectedChain,
    walletProvider,
    isWalletNetworkAligned,
    selectedCoin,
    amount,
    address,
    isSelectedChainConfigured,
    selectedChainKey,
    walletContext,
  ]);

  useEffect(() => {
    refreshAllowance();
  }, [refreshAllowance]);

  useEffect(() => {
    if (
      isConnected &&
      (status === 'Select a Solana wallet to continue.' ||
        status === 'Select a wallet to continue.')
    ) {
      setStatus('Wallet connected');
      setStatusType('success');
    } else if (!isConnected && status === 'Wallet connected') {
      setStatus('');
      setStatusType('');
    }
  }, [isConnected, status]);

  const handleApprove = useCallback(async () => {
    if (!capabilities.requiresApproval) {
      setStatus('Approval is not required on this chain.');
      setStatusType('info');
      return;
    }
    if (!walletProvider) {
      setStatus('EVM wallet not detected');
      setStatusType('error');
      return;
    }
    if (!isWalletNetworkAligned) {
      setStatus(`Please switch to ${selectedChain?.networkLabel || 'the selected mainnet'}`);
      setStatusType('error');
      return;
    }
    if (!isSelectedChainConfigured) {
      setStatus(chainReadinessMessage);
      setStatusType('error');
      return;
    }
    const validation = validateAmount(amount, stablecoinBalance, decimals);
    if (!validation.valid) {
      setStatus(validation.error);
      setStatusType('error');
      return;
    }
    setTransactionHash('');
    setIsLoading(true);
    setStatus('Approving...');
    setStatusType('info');
    try {
      const result = await approveIfRequired({
        chainKey: selectedChainKey,
        walletContext,
        inputAsset: selectedCoin,
        amount,
      });
      if (result.txHash) setTransactionHash(result.txHash);
      setStatus(result.status);
      setStatusType(result.statusType);
      if (result.ok) {
        await refreshAllowance();
        await refreshBalances();
      }
    } catch (error) {
      setStatus(parseError(error));
      setStatusType('error');
    } finally {
      setIsLoading(false);
    }
  }, [
    capabilities.requiresApproval,
    walletProvider,
    isWalletNetworkAligned,
    selectedChain,
    isSelectedChainConfigured,
    chainReadinessMessage,
    amount,
    stablecoinBalance,
    decimals,
    selectedChainKey,
    walletContext,
    selectedCoin,
    refreshAllowance,
    refreshBalances,
  ]);

  const handleSwap = useCallback(async () => {
    if (pendingSwapHash) {
      setTransactionHash(pendingSwapHash);
      setStatus(pendingSwapMessage);
      setStatusType('error');
      return;
    }
    if (selectedChain?.adapter === 'evm' && !walletProvider) {
      setStatus('EVM wallet not detected');
      setStatusType('error');
      return;
    }
    if (!isConnected) {
      setStatus('Connect a wallet first');
      setStatusType('error');
      return;
    }
    if (!isWalletNetworkAligned) {
      setStatus(`Please switch to ${selectedChain?.networkLabel || 'the selected mainnet'}`);
      setStatusType('error');
      return;
    }
    if (!isSelectedChainConfigured) {
      setStatus(chainReadinessMessage);
      setStatusType('error');
      return;
    }
    const validation = validateAmount(amount, stablecoinBalance, decimals);
    if (!validation.valid) {
      setStatus(validation.error);
      setStatusType('error');
      return;
    }
    setTransactionHash('');
    setStatus(`You are about to swap ${amount} ${selectedCoin?.name} for ${amount} USDN`);
    setStatusType('info');
    await new Promise(resolve => setTimeout(resolve, 300));
    setIsLoading(true);
    try {
      setStatus('Running pre-flight checks...');
      await simulateSwap({
        chainKey: selectedChainKey,
        walletContext,
        inputAsset: selectedCoin,
        amount,
      });
      setStatus('Submitting transaction...');
      const result = await submitSwap({
        chainKey: selectedChainKey,
        walletContext,
        inputAsset: selectedCoin,
        amount,
        onSubmitted: hash => {
          setTransactionHash(hash);
          setPendingSwapHash(hash);
          if (pendingSwapStorageKey) {
            try {
              window.localStorage.setItem(pendingSwapStorageKey, hash);
            } catch {
              // localStorage can be unavailable in privacy modes; state still guards the session.
            }
          }
          setStatus('Transaction submitted. Waiting for confirmation...');
          setStatusType('info');
        },
      });
      if (result.txHash) setTransactionHash(result.txHash);
      setStatus(result.status);
      setStatusType(result.statusType);
      if (result.ok) {
        setPendingSwapHash('');
        if (pendingSwapStorageKey) {
          try {
            window.localStorage.removeItem(pendingSwapStorageKey);
          } catch {
            // Ignore storage cleanup failures.
          }
        }
        setAmount('');
        await refreshBalances();
        await refreshAllowance();
      } else if (result.submitted) {
        await refreshBalances();
        await refreshAllowance();
        if (result.finalized) {
          setPendingSwapHash('');
          if (pendingSwapStorageKey) {
            try {
              window.localStorage.removeItem(pendingSwapStorageKey);
            } catch {
              // Ignore storage cleanup failures.
            }
          }
        }
      }
    } catch (error) {
      setStatus(parseError(error));
      setStatusType('error');
    } finally {
      setIsLoading(false);
    }
  }, [
    selectedChainKey,
    selectedChain,
    isSelectedChainConfigured,
    chainReadinessMessage,
    pendingSwapHash,
    pendingSwapMessage,
    pendingSwapStorageKey,
    walletProvider,
    isConnected,
    isWalletNetworkAligned,
    amount,
    stablecoinBalance,
    decimals,
    selectedCoin,
    walletContext,
    refreshBalances,
    refreshAllowance,
  ]);

  const handleConnect = useCallback(async () => {
    try {
      const connectedAddress = await connect();
      if (connectedAddress) {
        setStatus('Wallet connected');
        setStatusType('success');
      } else if (selectedChain?.adapter === 'solana') {
        setStatus(
          'Select a wallet that exposes a Solana account. EVM-only connections do not connect the Solana swap account.'
        );
        setStatusType('info');
      } else {
        setStatus('Select a wallet to continue.');
        setStatusType('info');
      }
    } catch (error) {
      setStatus(parseError(error));
      setStatusType('error');
    }
  }, [connect, selectedChain]);

  const handleDisconnect = useCallback(async () => {
    try {
      await disconnect();
    } finally {
      setStatus('');
      setStatusType('');
      setTransactionHash('');
    }
  }, [disconnect]);

  const handleSwitchNetwork = useCallback(async () => {
    dismissedNetworkSwitchKeyRef.current = `${address || 'unknown'}:${selectedChainKey}:${currentChainId || 'unknown'}`;
    setStatus(`Switching to ${toggleTargetNetworkName || 'target network'}...`);
    setStatusType('info');
    setIsLoading(true);
    try {
      const result = await switchNetwork();
      if (result && result.success) {
        setStatus(`Switched to ${result.targetNetworkName || 'target network'} successfully!`);
        setStatusType('success');
        setIsNetworkSwitchModalOpen(false);
      } else {
        setStatus('Failed to switch network. Please try again.');
        setStatusType('error');
      }
      return result;
    } catch (error) {
      setStatus(parseError(error));
      setStatusType('error');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [address, currentChainId, selectedChainKey, switchNetwork, toggleTargetNetworkName]);

  const handleCloseNetworkSwitchModal = useCallback(() => {
    dismissedNetworkSwitchKeyRef.current = `${address || 'unknown'}:${selectedChainKey}:${currentChainId || 'unknown'}`;
    setIsNetworkSwitchModalOpen(false);
  }, [address, currentChainId, selectedChainKey]);

  const handleConnectOrSwap = useCallback(async () => {
    if (!isConnected) {
      await handleConnect();
      return;
    }
    if (!isWalletNetworkAligned) {
      await handleSwitchNetwork();
      return;
    }
    if (!capabilities.requiresApproval || allowanceEnough) {
      handleSwap();
    } else {
      handleApprove();
    }
  }, [
    isConnected,
    isWalletNetworkAligned,
    capabilities.requiresApproval,
    allowanceEnough,
    handleConnect,
    handleSwitchNetwork,
    handleSwap,
    handleApprove,
  ]);

  const handleAmountChange = useCallback(e => {
    const value = sanitizeAmount(e.target.value);
    setAmount(value);
  }, []);

  const handleStablecoinSelect = useCallback(nextStablecoin => {
    setSelectedStablecoin(nextStablecoin);
    setStatus('');
    setStatusType('');
    setTransactionHash('');
  }, []);

  const handleChainSelect = useCallback(chainKey => {
    setSelectedChainKey(chainKey);
    try {
      window.localStorage.setItem(SELECTED_CHAIN_STORAGE_KEY, chainKey);
    } catch {
      // Chain selection still works for the current session.
    }
    setStatus('');
    setStatusType('');
    setTransactionHash('');
    setAmount('');
    setIsChainMenuOpen(false);
  }, []);

  const handleConfirmMax = useCallback(() => {
    setAmount(stablecoinBalance);
    setShowMaxModal(false);
  }, [stablecoinBalance]);

  const handleClearPendingSwap = useCallback(() => {
    setPendingSwapHash('');
    setTransactionHash('');
    if (pendingSwapStorageKey) {
      try {
        window.localStorage.removeItem(pendingSwapStorageKey);
      } catch {
        // Ignore storage cleanup failures.
      }
    }
    setStatus(
      selectedChain?.adapter === 'evm'
        ? 'Local queued swap state cleared. Only do this after canceling or clearing the transaction in your wallet.'
        : 'Local submitted swap state cleared. Only do this after verifying the transaction in your wallet or explorer.'
    );
    setStatusType('info');
  }, [pendingSwapStorageKey, selectedChain]);

  const handleCopyAddress = useCallback(async () => {
    if (!address) return;
    const success = await copyToClipboard(address);
    if (success) {
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 2000);
    }
  }, [address]);

  const getButtonText = useCallback(() => {
    if (!isConnected) return `${connectButtonText} to Swap`;
    if (!isWalletNetworkAligned) return 'Switch Network';
    if (!isSelectedChainConfigured) return 'Unavailable';
    if (pendingSwapHash) {
      return selectedChain?.adapter === 'evm' ? 'Queued in wallet' : 'Transaction submitted';
    }
    if (allowanceLoading) return 'Checking approval...';
    if (!capabilities.requiresApproval || allowanceEnough) return 'Swap';
    return 'Approve';
  }, [
    isConnected,
    connectButtonText,
    isWalletNetworkAligned,
    isSelectedChainConfigured,
    pendingSwapHash,
    selectedChain?.adapter,
    allowanceLoading,
    capabilities.requiresApproval,
    allowanceEnough,
  ]);

  const isSwapButtonDisabled = useMemo(() => {
    return (
      isLoading ||
      allowanceLoading ||
      Boolean(pendingSwapHash) ||
      !isSelectedChainConfigured ||
      (isConnected &&
        isWalletNetworkAligned &&
        (!amount || Number(amount) <= 0 || !!amountValidation.error || stablecoinLoading))
    );
  }, [
    isLoading,
    allowanceLoading,
    pendingSwapHash,
    isSelectedChainConfigured,
    isConnected,
    isWalletNetworkAligned,
    amount,
    amountValidation.error,
    stablecoinLoading,
  ]);

  const handleDiagnosticsPopoutBlocked = useCallback(msg => {
    setStatus(msg);
    setStatusType('error');
  }, []);

  const diagnostics = useDiagnostics({
    selectedChain,
    selectedChainKey,
    networkDisplayName,
    currentChainId,
    isWalletNetworkAligned,
    isCorrectNetwork,
    isNetworkSwitchModalOpen,
    isConnected,
    address,
    selectedStablecoin,
    selectedCoin,
    solanaBalanceTargets,
    solanaWallet,
    walletProviderLabel,
    balanceDiagnostics,
    stablecoinBalance,
    stablecoinLoading,
    usdnBalance,
    usdnLoading,
    onPopoutBlocked: handleDiagnosticsPopoutBlocked,
  });

  return {
    // Chain & Token config
    enabledChains,
    comingSoonChains,
    selectedChainKey,
    selectedChain,
    selectedStablecoin,
    selectedCoin,
    balanceLabel,
    decimals,
    capabilities,
    isSelectedChainConfigured,
    chainReadinessMessage,
    networkDisplayName,
    isWalletNetworkAligned,
    connectButtonText,

    // Wallet state
    address,
    isConnected,
    disconnect: handleDisconnect,
    handleDisconnect,
    currentChainId,
    currentNetworkName,
    toggleTargetNetworkName,
    copyFeedback,
    handleCopyAddress,

    // Chain selector UI state
    isChainMenuOpen,
    setIsChainMenuOpen,
    handleChainSelect,

    // Form & Swap state
    amount,
    amountValidation,
    stablecoinBalance,
    usdnBalance,
    stablecoinLoading,
    usdnLoading,
    allowanceEnough,
    allowanceLoading,
    isLoading,
    transactionHash,
    pendingSwapHash,
    pendingSwapMessage,
    pendingSwapClearLabel,
    status,
    statusType,
    getButtonText,
    isSwapButtonDisabled,

    // Modals
    showMaxModal,
    setShowMaxModal,
    isNetworkSwitchModalOpen,
    handleCloseNetworkSwitchModal,

    // Handlers
    handleAmountChange,
    handleStablecoinSelect,
    handleConfirmMax,
    handleClearPendingSwap,
    handleConnect,
    handleSwitchNetwork,
    handleApprove,
    handleSwap,
    handleConnectOrSwap,

    // Diagnostics
    diagnostics,
  };
};
